import { useState } from "react";
import { api, useApi } from "../api.js";
import { usePermission } from "../auth-context.js";
import { FullScreenLoader, Notice, useToast } from "../components.js";
import { useStickyState } from "../useStickyState.js";
import type { ChapterNode, ChapterTree, Named, SubChapterNode, TopicNode } from "./generation-targets.js";

/**
 * Create/edit for chapter → sub-chapter → topic, native to ZenForge.
 *
 * Deliberately minimal: name + sequence number + parent link only. Everything
 * else on the main app's own forms (PDF upload, page numbers, book content,
 * notes, status/deactivation) stays out of scope — ZenForge's workflow never
 * reads those fields, and admin-portal remains the place for them. Writes are
 * direct against the main app (no local staging), the same as admin-portal —
 * a chapter/topic edit is structure, not content awaiting review.
 */
export function Syllabus() {
  const { toast, element: toastElement } = useToast();
  const canCreate = usePermission("hierarchy:create");
  const canEdit = usePermission("hierarchy:edit");
  const canDelete = usePermission("hierarchy:delete");

  const [board, setBoard] = useStickyState<string | null>("zf.syllabus.board", null);
  const [className, setClassName] = useStickyState<string | null>("zf.syllabus.class", null);
  const [subject, setSubject] = useStickyState<string | null>("zf.syllabus.subject", null);
  const [refreshKey, setRefreshKey] = useState(0);
  const refresh = () => setRefreshKey((k) => k + 1);

  const boards = useApi<Named[]>("/boards");
  const classes = useApi<Named[]>("/classes");
  const subjects = useApi<Named[]>("/subjects");

  const tree = useApi<ChapterTree>(
    board && className && subject
      ? `/chapter-tree?board=${encodeURIComponent(board)}&class=${encodeURIComponent(className)}&subject=${encodeURIComponent(subject)}`
      : null,
    [board, className, subject, refreshKey],
  );

  const [expandedChapter, setExpandedChapter] = useState<string | null>(null);
  const [expandedSubChapter, setExpandedSubChapter] = useState<string | null>(null);

  const [chapterForm, setChapterForm] = useState<{ id: string | null; name: string; sequenceNo: string } | null>(
    null,
  );
  const [subChapterForm, setSubChapterForm] = useState<
    { id: string | null; chapterId: string; name: string; sequenceNo: string } | null
  >(null);
  const [topicForm, setTopicForm] = useState<
    { id: string | null; subChapterId: string; name: string; sequenceNo: string } | null
  >(null);
  const [saving, setSaving] = useState(false);

  const apiError = boards.error ?? classes.error ?? subjects.error;

  async function saveChapter() {
    if (!chapterForm || !board || !className || !subject) return;
    const name = chapterForm.name.trim();
    if (!name) return;
    const sequenceNo = chapterForm.sequenceNo.trim() ? Number(chapterForm.sequenceNo) : undefined;

    setSaving(true);
    try {
      if (chapterForm.id) {
        await api.put(`/chapters/${chapterForm.id}`, { name, sequenceNo });
        toast(`Saved "${name}"`);
      } else {
        await api.post("/chapters", { board, class: className, subject, name, sequenceNo });
        toast(`Created chapter "${name}"`);
      }
      setChapterForm(null);
      refresh();
    } catch (err) {
      toast(err instanceof Error ? err.message : "Failed to save chapter");
    } finally {
      setSaving(false);
    }
  }

  async function saveSubChapter() {
    if (!subChapterForm) return;
    const name = subChapterForm.name.trim();
    if (!name) return;
    const sequenceNo = subChapterForm.sequenceNo.trim() ? Number(subChapterForm.sequenceNo) : undefined;

    setSaving(true);
    try {
      if (subChapterForm.id) {
        await api.put(`/sub-chapters/${subChapterForm.id}`, { name, sequenceNo });
        toast(`Saved "${name}"`);
      } else {
        await api.post("/sub-chapters", { chapterId: subChapterForm.chapterId, name, sequenceNo });
        toast(`Created sub-chapter "${name}"`);
      }
      setSubChapterForm(null);
      refresh();
    } catch (err) {
      toast(err instanceof Error ? err.message : "Failed to save sub-chapter");
    } finally {
      setSaving(false);
    }
  }

  async function saveTopic() {
    if (!topicForm) return;
    const name = topicForm.name.trim();
    if (!name) return;
    const sequenceNo = topicForm.sequenceNo.trim() ? Number(topicForm.sequenceNo) : undefined;

    setSaving(true);
    try {
      if (topicForm.id) {
        await api.put(`/topics/${topicForm.id}`, { name, sequenceNo });
        toast(`Saved "${name}"`);
      } else {
        await api.post("/topics", { subChapterId: topicForm.subChapterId, name, sequenceNo });
        toast(`Created topic "${name}"`);
      }
      setTopicForm(null);
      refresh();
    } catch (err) {
      toast(err instanceof Error ? err.message : "Failed to save topic");
    } finally {
      setSaving(false);
    }
  }

  // Sub-chapter and topic deletes are hard deletes on the main app with no
  // undo — a sub-chapter delete also removes its topics and all of their
  // content, so the confirmation says so explicitly. Chapter delete is
  // intentionally not offered here: too destructive to expose without the
  // main app's own safeguards.
  async function deleteSubChapter(sc: SubChapterNode) {
    const topicCount = sc.topics.length;
    const warning = topicCount > 0 ? ` This also deletes its ${topicCount} topic${topicCount === 1 ? "" : "s"} and all of their content.` : "";
    if (!confirm(`Delete sub-chapter "${sc.name}"?${warning} This cannot be undone.`)) return;

    try {
      await api.delete(`/sub-chapters/${sc.id}`);
      toast(`Deleted "${sc.name}"`);
      if (expandedSubChapter === sc.id) setExpandedSubChapter(null);
      refresh();
    } catch (err) {
      toast(err instanceof Error ? err.message : "Failed to delete sub-chapter");
    }
  }

  async function deleteTopic(topic: TopicNode) {
    if (!confirm(`Delete topic "${topic.name}"? This cannot be undone.`)) return;

    try {
      await api.delete(`/topics/${topic.id}`);
      toast(`Deleted "${topic.name}"`);
      refresh();
    } catch (err) {
      toast(err instanceof Error ? err.message : "Failed to delete topic");
    }
  }

  return (
    <div className="stack" style={{ gap: 16 }}>
      {toastElement}
      <p className="muted" style={{ margin: 0 }}>
        Chapters, sub-chapters and topics live in the main app — this edits them directly, the same
        as admin-portal does. Changes take effect immediately; there is no draft or review step.
      </p>

      {apiError && <Notice kind="error">{apiError}</Notice>}

      <div className="card">
        <div className="row" style={{ gap: 16, alignItems: "flex-end", flexWrap: "wrap" }}>
          <div className="field" style={{ flex: 1, minWidth: 140, margin: 0 }}>
            <label>Board</label>
            <select
              value={board ?? ""}
              onChange={(e) => {
                setBoard(e.target.value || null);
                setExpandedChapter(null);
                setExpandedSubChapter(null);
              }}
            >
              <option value="">Select a board…</option>
              {boards.data?.map((b) => (
                <option key={b.id} value={b.name}>
                  {b.name}
                </option>
              ))}
            </select>
          </div>
          <div className="field" style={{ flex: 1, minWidth: 140, margin: 0 }}>
            <label>Class</label>
            <select
              value={className ?? ""}
              onChange={(e) => {
                setClassName(e.target.value || null);
                setExpandedChapter(null);
                setExpandedSubChapter(null);
              }}
            >
              <option value="">Select a class…</option>
              {classes.data?.map((c) => (
                <option key={c.id} value={c.name}>
                  {c.name}
                </option>
              ))}
            </select>
          </div>
          <div className="field" style={{ flex: 1, minWidth: 160, margin: 0 }}>
            <label>Subject</label>
            <select
              value={subject ?? ""}
              onChange={(e) => {
                setSubject(e.target.value || null);
                setExpandedChapter(null);
                setExpandedSubChapter(null);
              }}
            >
              <option value="">Select a subject…</option>
              {subjects.data?.map((s) => (
                <option key={s.id} value={s.name}>
                  {s.name}
                </option>
              ))}
            </select>
          </div>
          {board && className && subject && canCreate && (
            <button
              className="btn primary"
              onClick={() => setChapterForm({ id: null, name: "", sequenceNo: "" })}
            >
              + New chapter
            </button>
          )}
        </div>
      </div>

      {tree.loading && <FullScreenLoader label="Loading syllabus…" />}

      {board && className && subject && !tree.loading && !tree.data && (
        <Notice kind="info">
          No syllabus for {board} / {className} / {subject} in the main app yet. Create the first
          chapter above to start one.
        </Notice>
      )}

      {tree.data && (
        <div className="card">
          {tree.data.chapters.length === 0 && (
            <p className="muted">No chapters yet.</p>
          )}
          <div className="stack" style={{ gap: 4 }}>
            {tree.data.chapters
              .slice()
              .sort((a, b) => (a.sequenceNo ?? 0) - (b.sequenceNo ?? 0))
              .map((chapter) => (
                <ChapterRow
                  key={chapter.id}
                  chapter={chapter}
                  expanded={expandedChapter === chapter.id}
                  onToggle={() =>
                    setExpandedChapter((cur) => (cur === chapter.id ? null : chapter.id))
                  }
                  expandedSubChapter={expandedSubChapter}
                  onToggleSubChapter={(id) =>
                    setExpandedSubChapter((cur) => (cur === id ? null : id))
                  }
                  onEditChapter={() =>
                    setChapterForm({
                      id: chapter.id,
                      name: chapter.name,
                      sequenceNo: chapter.sequenceNo != null ? String(chapter.sequenceNo) : "",
                    })
                  }
                  onAddSubChapter={() =>
                    setSubChapterForm({ id: null, chapterId: chapter.id, name: "", sequenceNo: "" })
                  }
                  onEditSubChapter={(sc) =>
                    setSubChapterForm({
                      id: sc.id,
                      chapterId: chapter.id,
                      name: sc.name,
                      sequenceNo: sc.sequenceNo != null ? String(sc.sequenceNo) : "",
                    })
                  }
                  onAddTopic={(subChapterId) =>
                    setTopicForm({ id: null, subChapterId, name: "", sequenceNo: "" })
                  }
                  onEditTopic={(subChapterId, topic) =>
                    setTopicForm({
                      id: topic.id,
                      subChapterId,
                      name: topic.name,
                      sequenceNo: topic.sequenceNo != null ? String(topic.sequenceNo) : "",
                    })
                  }
                  onDeleteSubChapter={(sc) => void deleteSubChapter(sc)}
                  onDeleteTopic={(topic) => void deleteTopic(topic)}
                  canCreate={canCreate}
                  canEdit={canEdit}
                  canDelete={canDelete}
                />
              ))}
          </div>
        </div>
      )}

      {chapterForm && (
        <FormModal
          title={chapterForm.id ? "Edit chapter" : "New chapter"}
          subtitle={chapterForm.id ? undefined : `${board} · ${className} · ${subject}`}
          name={chapterForm.name}
          sequenceNo={chapterForm.sequenceNo}
          onNameChange={(name) => setChapterForm((f) => (f ? { ...f, name } : f))}
          onSequenceChange={(sequenceNo) => setChapterForm((f) => (f ? { ...f, sequenceNo } : f))}
          onCancel={() => setChapterForm(null)}
          onSave={() => void saveChapter()}
          saving={saving}
        />
      )}

      {subChapterForm && (
        <FormModal
          title={subChapterForm.id ? "Edit sub-chapter" : "New sub-chapter"}
          name={subChapterForm.name}
          sequenceNo={subChapterForm.sequenceNo}
          onNameChange={(name) => setSubChapterForm((f) => (f ? { ...f, name } : f))}
          onSequenceChange={(sequenceNo) => setSubChapterForm((f) => (f ? { ...f, sequenceNo } : f))}
          onCancel={() => setSubChapterForm(null)}
          onSave={() => void saveSubChapter()}
          saving={saving}
        />
      )}

      {topicForm && (
        <FormModal
          title={topicForm.id ? "Edit topic" : "New topic"}
          name={topicForm.name}
          sequenceNo={topicForm.sequenceNo}
          onNameChange={(name) => setTopicForm((f) => (f ? { ...f, name } : f))}
          onSequenceChange={(sequenceNo) => setTopicForm((f) => (f ? { ...f, sequenceNo } : f))}
          onCancel={() => setTopicForm(null)}
          onSave={() => void saveTopic()}
          saving={saving}
        />
      )}
    </div>
  );
}

function ChapterRow({
  chapter,
  expanded,
  onToggle,
  expandedSubChapter,
  onToggleSubChapter,
  onEditChapter,
  onAddSubChapter,
  onEditSubChapter,
  onAddTopic,
  onEditTopic,
  onDeleteSubChapter,
  onDeleteTopic,
  canCreate,
  canEdit,
  canDelete,
}: {
  chapter: ChapterNode;
  expanded: boolean;
  onToggle: () => void;
  expandedSubChapter: string | null;
  onToggleSubChapter: (id: string) => void;
  onEditChapter: () => void;
  onAddSubChapter: () => void;
  onEditSubChapter: (sc: SubChapterNode) => void;
  onAddTopic: (subChapterId: string) => void;
  onEditTopic: (subChapterId: string, topic: TopicNode) => void;
  onDeleteSubChapter: (sc: SubChapterNode) => void;
  onDeleteTopic: (topic: TopicNode) => void;
  canCreate: boolean;
  canEdit: boolean;
  canDelete: boolean;
}) {
  return (
    <div className="tree-node">
      <div
        className="row syllabus-row"
        style={{ gap: 8, padding: "6px 8px", alignItems: "center", cursor: "pointer" }}
        onClick={onToggle}
      >
        <button className="btn mini" style={{ width: 22, pointerEvents: "none" }} tabIndex={-1} aria-hidden>
          {expanded ? "−" : "+"}
        </button>
        <strong style={{ flex: 1 }}>
          {chapter.sequenceNo != null && <span className="muted mini">{chapter.sequenceNo}. </span>}
          {chapter.name}
        </strong>
        {canEdit && (
          <IconButton label="Edit chapter" icon="edit" onClick={(e) => { e.stopPropagation(); onEditChapter(); }} />
        )}
        {canCreate && (
          <IconButton
            label="Add sub-chapter"
            icon="add"
            onClick={(e) => { e.stopPropagation(); onAddSubChapter(); }}
          />
        )}
      </div>

      {expanded && (
        <div style={{ marginLeft: 30, borderLeft: "1px solid var(--border, #e4dfd4)", paddingLeft: 12 }}>
          {chapter.subChapters.length === 0 && <p className="muted mini">No sub-chapters yet.</p>}
          {chapter.subChapters
            .slice()
            .sort((a, b) => (a.sequenceNo ?? 0) - (b.sequenceNo ?? 0))
            .map((sc) => (
              <div key={sc.id}>
                <div
                  className="row syllabus-row"
                  style={{ gap: 8, padding: "5px 8px", alignItems: "center", cursor: "pointer" }}
                  onClick={() => onToggleSubChapter(sc.id)}
                >
                  <button className="btn mini" style={{ width: 22, pointerEvents: "none" }} tabIndex={-1} aria-hidden>
                    {expandedSubChapter === sc.id ? "−" : "+"}
                  </button>
                  <span style={{ flex: 1 }}>
                    {sc.sequenceNo != null && <span className="muted mini">{sc.sequenceNo}. </span>}
                    {sc.name}
                  </span>
                  {canEdit && (
                    <IconButton
                      label="Edit sub-chapter"
                      icon="edit"
                      onClick={(e) => { e.stopPropagation(); onEditSubChapter(sc); }}
                    />
                  )}
                  {canCreate && (
                    <IconButton
                      label="Add topic"
                      icon="add"
                      onClick={(e) => { e.stopPropagation(); onAddTopic(sc.id); }}
                    />
                  )}
                  {canDelete && (
                    <IconButton
                      label="Delete sub-chapter"
                      icon="delete"
                      danger
                      onClick={(e) => { e.stopPropagation(); onDeleteSubChapter(sc); }}
                    />
                  )}
                </div>

                {expandedSubChapter === sc.id && (
                  <div
                    style={{ marginLeft: 30, borderLeft: "1px solid var(--border, #e4dfd4)", paddingLeft: 12 }}
                  >
                    {sc.topics.length === 0 && <p className="muted mini">No topics yet.</p>}
                    {sc.topics
                      .slice()
                      .sort((a, b) => (a.sequenceNo ?? 0) - (b.sequenceNo ?? 0))
                      .map((topic) => (
                        <div
                          key={topic.id}
                          className="row syllabus-row"
                          style={{ gap: 8, padding: "4px 8px", alignItems: "center" }}
                        >
                          <span style={{ flex: 1 }}>
                            {topic.sequenceNo != null && (
                              <span className="muted mini">{topic.sequenceNo}. </span>
                            )}
                            {topic.name}
                          </span>
                          {canEdit && (
                            <IconButton
                              label="Edit topic"
                              icon="edit"
                              onClick={() => onEditTopic(sc.id, topic)}
                            />
                          )}
                          {canDelete && (
                            <IconButton
                              label="Delete topic"
                              icon="delete"
                              danger
                              onClick={() => onDeleteTopic(topic)}
                            />
                          )}
                        </div>
                      ))}
                  </div>
                )}
              </div>
            ))}
        </div>
      )}
    </div>
  );
}

const ICON_PATHS: Record<"edit" | "add" | "delete", string> = {
  edit: "M11 4h2v2h-2zM3 17.25V21h3.75L17.81 9.94l-3.75-3.75zM20.71 7.04a1 1 0 0 0 0-1.41l-2.34-2.34a1 1 0 0 0-1.41 0l-1.83 1.83 3.75 3.75z",
  add: "M11 5h2v6h6v2h-6v6h-2v-6H5v-2h6z",
  delete: "M6 7h12v2H6zM9 3h6v2H9zM7 10h2v9H7zm4 0h2v9h-2zm4 0h2v9h-2z",
};

/** Small square icon button with a native tooltip (title) and an accessible
 *  label — the syllabus tree is dense enough that "Edit" / "Delete" text on
 *  every row crowded the row and made the hierarchy harder to scan. */
function IconButton({
  label,
  icon,
  onClick,
  danger,
}: {
  label: string;
  icon: "edit" | "add" | "delete";
  onClick: (e: React.MouseEvent) => void;
  danger?: boolean;
}) {
  return (
    <button
      className={`btn icon-btn mini${danger ? " danger" : " secondary"}`}
      onClick={onClick}
      title={label}
      aria-label={label}
    >
      <svg width="14" height="14" viewBox="0 0 24 24" fill="currentColor" aria-hidden="true">
        <path d={ICON_PATHS[icon]} />
      </svg>
    </button>
  );
}

function FormModal({
  title,
  subtitle,
  name,
  sequenceNo,
  onNameChange,
  onSequenceChange,
  onCancel,
  onSave,
  saving,
}: {
  title: string;
  subtitle?: string;
  name: string;
  sequenceNo: string;
  onNameChange: (name: string) => void;
  onSequenceChange: (sequenceNo: string) => void;
  onCancel: () => void;
  onSave: () => void;
  saving: boolean;
}) {
  return (
    <div className="modal-backdrop" onClick={onCancel}>
      <div className="modal-card" style={{ maxWidth: 440, width: "95%" }} onClick={(e) => e.stopPropagation()}>
        <h3 style={{ margin: 0 }}>{title}</h3>
        {subtitle && (
          <div className="muted mini" style={{ marginTop: 2, marginBottom: 12 }}>
            {subtitle}
          </div>
        )}

        <div className="field" style={{ marginTop: subtitle ? 0 : 12 }}>
          <label>Name</label>
          <input value={name} onChange={(e) => onNameChange(e.target.value)} autoFocus />
        </div>

        <div className="field">
          <label>Sequence number</label>
          <input
            type="number"
            value={sequenceNo}
            onChange={(e) => onSequenceChange(e.target.value)}
            placeholder="Auto-assigned if left blank"
          />
        </div>

        <div className="row justify-end gap-2" style={{ marginTop: 16 }}>
          <button className="btn secondary" onClick={onCancel}>
            Cancel
          </button>
          <button className="btn primary" disabled={!name.trim() || saving} onClick={onSave}>
            {saving ? "Saving…" : "Save"}
          </button>
        </div>
      </div>
    </div>
  );
}
